import { Rect, RectCoords } from '../types/Rect';
/**
 * Util for ensuring a number is >= 0.
 * Returns the input value if it's a positive number, otherwise returns `0`.
 */
const zeroBound = (num: number): number => Math.max(0, num);

export const makeRectCoords = (
  top: number,
  right: number,
  bottom: number,
  left: number
): RectCoords => ({
  top,
  right,
  bottom,
  left,
});

/** Creates a Rect */
export const makeRect = (
  top: number,
  right: number,
  bottom: number,
  left: number
): Rect => ({
  width: right - left,
  height: bottom - top,
  top,
  right,
  bottom,
  left,
});

export const makeRectFromCoords = ({
  top,
  right,
  bottom,
  left,
}: RectCoords): Rect => makeRect(top, right, bottom, left);

/**
 * Returns a copy of the given `rect` moved `x` px to the right and `y` px down
 */
export const translateRect = (rect: Rect, x: number, y: number): Rect =>
  makeRect(rect.top + y, rect.right + x, rect.bottom + y, rect.left + x);

/** Returns a copy of the given Rect */
export const copyRect = (rect: Rect): Rect =>
  makeRect(rect.top, rect.right, rect.bottom, rect.left);

/**
 * Returns a Rect that indicates how much overlap there is with the constraint on each side
 */
export const getCollisionRect = (rect: Rect, constraintRect: Rect): Rect =>
  makeRect(
    zeroBound(constraintRect.top - rect.top),
    zeroBound(rect.right - constraintRect.right),
    zeroBound(rect.bottom - constraintRect.bottom),
    zeroBound(constraintRect.left - rect.left)
  );

/** Subtracts the given `[top, right, bottom, left]` values from a rect, shrinking it. */
export const clipRectByValues = (
  rect: Rect,
  top: number,
  right: number,
  bottom: number,
  left: number
): Rect =>
  makeRect(
    rect.top + top,
    rect.right - right,
    rect.bottom - bottom,
    rect.left + left
  );
